// Recursive renderer for explicit canonical document sections.

import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { useLocation } from 'react-router';
import type { Document, Section } from '@airhead-games/shared/content';
import BlockRenderer from '@/content/rendering/renderers/BlockRenderer';
import RichTextRenderer from '@/content/rendering/renderers/RichTextRenderer';
import WidgetRenderer from '@/content/rendering/renderers/WidgetRenderer';
import {
  DOCUMENT_SECTION_NAVIGATION_EVENT,
  sectionTreeContainsId,
  type DocumentSectionNavigationDetail,
} from '@/content/lib/anchors';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/utilities/cn';

interface SectionNodeProps {
  section: Section;
  currentDocument: Document;
  level?: number;
  headingLevel?: number;
}

const headingClassName = (level: number) =>
  cn(
    'font-heading leading-tight font-semibold',
    level === 2 && 'text-2xl md:text-3xl',
    level === 3 && 'text-xl md:text-2xl',
    level >= 4 && 'text-lg md:text-xl'
  );

const Heading: React.FC<{
  id: string;
  headingLevel: number;
  children: React.ReactNode;
}> = ({ id, headingLevel, children }) => {
  const Tag = `h${Math.min(headingLevel, 6)}` as React.ElementType<{
    id: string;
    className: string;
    children: React.ReactNode;
  }>;
  return (
    <Tag id={id} className={headingClassName(headingLevel)}>
      {children}
    </Tag>
  );
};

const SectionDescription: React.FC<{
  nodes?: Section['description'];
  className?: string;
}> = ({ nodes, className }) => {
  if (!nodes?.length) {
    return null;
  }

  return (
    <span
      className={cn(
        'block text-sm leading-5 font-normal text-muted-foreground',
        className
      )}
    >
      <RichTextRenderer nodes={nodes} />
    </span>
  );
};

const SectionNode: React.FC<SectionNodeProps> = ({
  section,
  currentDocument,
  level = 2,
  headingLevel = level,
}) => {
  const location = useLocation();
  const hashId = location.hash.replace('#', '');
  const hashTargetsSection = useMemo(
    () => Boolean(hashId && sectionTreeContainsId(section, hashId)),
    [hashId, section]
  );
  const [open, setOpen] = useState(!section.collapsible || hashTargetsSection);
  const isReport = currentDocument.kind === 'report';
  const reportNestedClassName =
    isReport && level > 2 ? 'pl-3 sm:pl-4 md:pl-5' : undefined;
  const sectionDescriptionInsetClassName = level >= 4 ? 'pl-6' : 'pl-7';

  useEffect(() => {
    if (hashTargetsSection) {
      setOpen(true);
    }
  }, [hashTargetsSection]);

  useEffect(() => {
    if (!section.collapsible) {
      return undefined;
    }

    const handleSectionNavigation = (event: Event) => {
      const targetId = (event as CustomEvent<DocumentSectionNavigationDetail>)
        .detail?.id;

      if (targetId && sectionTreeContainsId(section, targetId)) {
        setOpen(true);
      }
    };

    window.addEventListener(
      DOCUMENT_SECTION_NAVIGATION_EVENT,
      handleSectionNavigation
    );

    return () => {
      window.removeEventListener(
        DOCUMENT_SECTION_NAVIGATION_EVENT,
        handleSectionNavigation
      );
    };
  }, [section]);

  const content = (
    <div className="flex flex-col gap-6">
      <BlockRenderer blocks={section.blocks} />
      <WidgetRenderer
        widgets={section.widgets}
        currentDocument={currentDocument}
      />
      {section.sections?.map((child) => (
        <SectionNode
          key={child.id}
          section={child}
          currentDocument={currentDocument}
          level={level + 1}
          headingLevel={
            headingLevel +
            (currentDocument.kind === 'blog' && !section.title ? 0 : 1)
          }
        />
      ))}
    </div>
  );

  if (section.collapsible && section.title) {
    return (
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className={cn(
          'flex flex-col',
          level === 2 &&
            'border-t border-border pt-8 first:border-t-0 first:pt-0',
          reportNestedClassName
        )}
      >
        <Heading id={section.id} headingLevel={headingLevel}>
          <CollapsibleTrigger
            className={cn(
              'group/section-trigger flex w-full items-center gap-2 rounded-md text-left text-[inherit] font-[inherit] leading-[inherit] whitespace-normal transition-colors outline-none hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/50',
              level === 2 && '-ml-2 px-2 py-1',
              level > 2 && '-ml-1 px-1 py-0.5'
            )}
          >
            <ChevronRightIcon
              data-icon="inline-start"
              className={cn(
                'size-5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/section-trigger:rotate-90',
                level >= 4 && 'size-4'
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <RichTextRenderer nodes={section.title} />
            </span>
          </CollapsibleTrigger>
        </Heading>
        <CollapsibleContent
          className={cn(
            'flex flex-col gap-6',
            section.description ? 'pt-1' : 'pt-6',
            !section.description && level > 2 && 'pt-5'
          )}
        >
          <SectionDescription
            nodes={section.description}
            className={sectionDescriptionInsetClassName}
          />
          {content}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <section
      className={cn(
        'flex flex-col gap-6',
        level === 2 &&
          'border-t border-border pt-8 first:border-t-0 first:pt-0',
        reportNestedClassName
      )}
    >
      {section.title && (
        <div className="flex flex-col gap-1">
          <Heading id={section.id} headingLevel={headingLevel}>
            <RichTextRenderer nodes={section.title} />
          </Heading>
          <SectionDescription nodes={section.description} />
        </div>
      )}
      {content}
    </section>
  );
};

export default SectionNode;
